import type {
  ConversationPartApiResponse,
  ConversationPartKind,
} from "@f2e/ui/chat-v2"

import type { FeedbackSystemType, Response } from "@/types"

import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import type {
  AIDraftToolSupportResponse,
  CreatePlatformReplyRequest,
  PlatformReplyRejectionResponse,
  PlatformReplyResponse,
} from "./histories"

type ChatV2MessageMetadata = ConversationPartApiResponse["metadata"]

export type ThreadStatus = "default" | "pending"

export interface ThreadSummaryResponse {
  id: string
  workspace_id: string
  representative_history_id: number
  latest_history_id: number
  title?: string | null
  latest_message_preview?: string | null
  last_activity_at: string
  source_type: string
  channel_type?: string | null
  history_count: number
  agent_id?: string | null
  agent_name?: string | null
  external_user_id?: string | null
  participant_name?: string | null
  channel?: {
    id?: string | null
    name?: string | null
    type: string
  } | null
  latest_message?: {
    actor: "participant" | "agent" | "workspace_member"
    actor_name?: string | null
    preview: string
    created_at: string
  } | null
  status: ThreadStatus
  needs_reply: boolean
  waiting_since?: string | null
  feedback_conversation_counts: Partial<Record<FeedbackSystemType, number>>
  total_cost_credits: number
  human_mode: boolean
  can_reply: boolean
  reply_disabled_reason?: string | null
  is_unread: boolean
}

export interface ThreadHistoryBoundaryResponse {
  history_id: number
  title?: string | null
  started_at: string
  last_activity_at: string
  agent_id?: string | null
  agent_name?: string | null
}

export interface ThreadDetailResponse extends ThreadSummaryResponse {
  histories: ThreadHistoryBoundaryResponse[]
  client_user_id?: string | null
  ai_draft_tool_support?: AIDraftToolSupportResponse | null
}

export interface ThreadAttachedFileResponse {
  id: string
  type: string
  name: string
  url: string
  scope: string
  attachment_type?: string
}

export interface ThreadConversationPartResponse {
  id?: number | null
  history_id: number
  conversation_id?: number | null
  group_id: string
  sequence: number
  part_kind: ConversationPartKind
  content: Record<string, unknown>
  metadata: Record<string, unknown>
  attached_files: ThreadAttachedFileResponse[]
  created_at?: string | null
  updated_at?: string | null
  source: string
  cursor: string
}

export interface ThreadPartsResponse {
  thread_key: string
  parts: ThreadConversationPartResponse[]
  boundaries: ThreadHistoryBoundaryResponse[]
  next_cursor?: string | null
  has_more: boolean
}

export interface UpdateThreadStateRequest {
  status: ThreadStatus
}

export interface ThreadStateResponse {
  status: ThreadStatus
}

export interface UpdateThreadReadStateRequest {
  through_part_id: number
}

export interface ThreadReadStateResponse {
  is_unread: boolean
  last_read_part_created_at: string
  last_read_part_id: number
}

const threadBasePath = (organizationId: string, workspaceId: string) =>
  `/organizations/${organizationId}/workspaces/${workspaceId}/threads`

export const threadPartResponseToConversationPart = (
  part: ThreadConversationPartResponse
): ConversationPartApiResponse => ({
  id: part.id ?? null,
  history_id: part.history_id,
  conversation_id: part.conversation_id,
  conversation_group_id: part.group_id,
  sequence: part.sequence,
  part_kind: part.part_kind,
  content: part.content,
  metadata: part.metadata as ChatV2MessageMetadata,
  source: "stack",
  created_at: part.created_at,
  updated_at: part.updated_at,
  attached_files: part.attached_files,
})

export const useGetThreadsApi = (organizationId: string, workspaceId: string) =>
  useCodeerApi<Response<ThreadSummaryResponse[]>>(
    threadBasePath(organizationId, workspaceId),
    FetchMethod.Get
  )

export const useGetThreadApi = (
  organizationId: string,
  workspaceId: string,
  threadKey: string
) =>
  useCodeerApi<Response<ThreadDetailResponse>>(
    `${threadBasePath(organizationId, workspaceId)}/${threadKey}`,
    FetchMethod.Get
  )

export const useGetThreadByHistoryApi = (
  organizationId: string,
  workspaceId: string,
  historyId: number
) =>
  useCodeerApi<Response<ThreadDetailResponse>>(
    `${threadBasePath(organizationId, workspaceId)}/by-history/${historyId}`,
    FetchMethod.Get
  )

export const useGetThreadPartsApi = (
  organizationId: string,
  workspaceId: string,
  threadKey: string
) =>
  useCodeerApi<Response<ThreadPartsResponse>>(
    `${threadBasePath(organizationId, workspaceId)}/${threadKey}/parts`,
    FetchMethod.Get
  )

export const useUpdateThreadStateApi = (
  organizationId: string,
  workspaceId: string,
  threadKey: string
) =>
  useCodeerApi<Response<ThreadStateResponse>, UpdateThreadStateRequest>(
    `${threadBasePath(organizationId, workspaceId)}/${threadKey}/state`,
    FetchMethod.Put
  )

export const useUpdateThreadReadStateApi = (
  organizationId: string,
  workspaceId: string,
  threadKey: string
) =>
  useCodeerApi<Response<ThreadReadStateResponse>, UpdateThreadReadStateRequest>(
    `${threadBasePath(organizationId, workspaceId)}/${threadKey}/read-state`,
    FetchMethod.Put
  )

export const useCreateThreadPlatformReplyApi = (
  organizationId: string,
  workspaceId: string,
  threadKey: string
) =>
  useCodeerApi<
    Response<PlatformReplyResponse | PlatformReplyRejectionResponse>,
    CreatePlatformReplyRequest
  >(
    `${threadBasePath(organizationId, workspaceId)}/${threadKey}/platform-replies`,
    FetchMethod.Post
  )
